/**
 * Specialized analysis for floor_plan section bloat
 * The floor_plan section can contain large linkedFloorPlanData
 */

export function getSize(obj: any): number {
  return new Blob([JSON.stringify(obj)]).size;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

export interface FloorPlanAnalysisResult {
  totalSize: number;
  floorPlanIdSize: number;
  linkedDataSize: number;
  linkedDataBreakdown: Array<{ field: string; size: number }>;
  hasLinkedData: boolean;
  recommendCleanup: boolean;
}

/**
 * Analyze floor_plan section to identify what's causing bloat
 */
export function analyzeFloorPlanSize(floorPlanData: any): FloorPlanAnalysisResult {
  if (!floorPlanData) {
    return {
      totalSize: 0,
      floorPlanIdSize: 0,
      linkedDataSize: 0,
      linkedDataBreakdown: [],
      hasLinkedData: false,
      recommendCleanup: false
    };
  }

  const totalSize = getSize(floorPlanData);
  const floorPlanIdSize = getSize({ floorPlanId: floorPlanData.floorPlanId });
  const linkedDataSize = floorPlanData.linkedFloorPlanData 
    ? getSize(floorPlanData.linkedFloorPlanData)
    : 0;

  const linkedDataBreakdown: Array<{ field: string; size: number }> = [];

  if (floorPlanData.linkedFloorPlanData) {
    Object.keys(floorPlanData.linkedFloorPlanData).forEach(key => {
      const fieldSize = getSize(floorPlanData.linkedFloorPlanData[key]);
      if (fieldSize > 10000) { // Only show fields > 10 KB
        linkedDataBreakdown.push({ field: key, size: fieldSize });
      }
    });
    linkedDataBreakdown.sort((a, b) => b.size - a.size);
  }

  return {
    totalSize,
    floorPlanIdSize,
    linkedDataSize,
    linkedDataBreakdown,
    hasLinkedData: !!floorPlanData.linkedFloorPlanData,
    recommendCleanup: linkedDataSize > 1024 * 1024 // Recommend cleanup if > 1 MB
  };
}

/**
 * Log floor plan analysis to console
 */
export function logFloorPlanAnalysis(result: FloorPlanAnalysisResult): void {
  console.log('');
  console.log('🏢 ========================================');
  console.log('🏢 [FLOOR_PLAN] SIZE ANALYSIS');
  console.log('🏢 ========================================');
  console.log(`🏢 Total Size: ${formatBytes(result.totalSize)}`);
  console.log(`🏢 Floor Plan ID: ${formatBytes(result.floorPlanIdSize)}`);
  console.log(`🏢 Linked Data: ${formatBytes(result.linkedDataSize)}`);
  console.log('🏢 ========================================');

  if (result.hasLinkedData) {
    console.log('🏢 LINKED DATA BREAKDOWN:');
    if (result.linkedDataBreakdown.length > 0) {
      result.linkedDataBreakdown.forEach(item => {
        const emoji = item.size > 1024 * 1024 ? '🔴' : item.size > 100000 ? '🟡' : '🟢';
        console.log(`${emoji} ${item.field}: ${formatBytes(item.size)}`);
      });
    } else {
      console.log('   No large fields found (all < 10 KB)');
    }
    console.log('🏢 ========================================');
  }

  if (result.recommendCleanup) {
    console.warn('⚠️ [RECOMMENDATION] linkedFloorPlanData is very large!');
    console.warn(`   ${formatBytes(result.linkedDataSize)} of floor plan data`);
    console.warn('   💡 Consider storing only floorPlanId and fetching data separately');
    console.warn('   💡 Or remove unnecessary fields from linkedFloorPlanData');
  } else if (result.hasLinkedData) {
    console.log('✅ Linked data size is reasonable');
  } else {
    console.log('✅ Only storing floorPlanId (optimal)');
  }

  console.log('🏢 ========================================');
  console.log('');
}

/**
 * Deep analysis of elements array to find what's causing bloat
 */
export function analyzeElementsArray(elements: any[]): void {
  if (!Array.isArray(elements)) {
    console.log('❌ elements is not an array');
    return;
  }

  console.log('');
  console.log('🔍 ========================================');
  console.log(`🔍 ELEMENTS ARRAY DEEP DIVE (${elements.length} items)`);
  console.log('🔍 ========================================');

  // Sample first few elements to understand structure
  const sampleSize = Math.min(5, elements.length);
  console.log(`🔍 Sampling first ${sampleSize} elements:`);

  elements.slice(0, sampleSize).forEach((element, idx) => {
    const elementSize = getSize(element);
    console.log(`\n  Element ${idx}:`);
    console.log(`    Size: ${formatBytes(elementSize)}`);
    console.log(`    Type: ${element?.type || 'unknown'}`);
    console.log(`    Keys: ${Object.keys(element || {}).join(', ')}`);
    
    // Check for base64 data
    const hasBase64 = JSON.stringify(element).includes('data:image');
    if (hasBase64) {
      console.log(`    ⚠️ Contains base64 image data!`);
    }
    
    // Check for large string fields
    Object.keys(element || {}).forEach(key => {
      const fieldValue = element[key];
      if (typeof fieldValue === 'string' && fieldValue.length > 1000) {
        const fieldSize = getSize(fieldValue);
        console.log(`    🔴 Large string field "${key}": ${formatBytes(fieldSize)}`);
        if (fieldValue.startsWith('data:image')) {
          console.log(`       ⚠️ This is base64 image data!`);
        }
      }
    });
  });

  // Calculate size distribution
  const sizes = elements.map(el => getSize(el));
  const totalSize = sizes.reduce((sum, size) => sum + size, 0);
  const avgSize = totalSize / elements.length;
  const maxSize = Math.max(...sizes);
  const minSize = Math.min(...sizes);

  console.log('\n🔍 SIZE STATISTICS:');
  console.log(`  Total: ${formatBytes(totalSize)}`);
  console.log(`  Average per element: ${formatBytes(avgSize)}`);
  console.log(`  Largest element: ${formatBytes(maxSize)}`);
  console.log(`  Smallest element: ${formatBytes(minSize)}`);

  // Find elements with base64
  const elementsWithBase64 = elements.filter(el => 
    JSON.stringify(el).includes('data:image')
  );
  
  if (elementsWithBase64.length > 0) {
    console.log(`\n⚠️ Found ${elementsWithBase64.length} elements with base64 image data!`);
    console.log('   These are likely causing the bloat');
  }

  console.log('🔍 ========================================');
  console.log('');
}

/**
 * Expose to window for manual testing
 */
export function exposeFloorPlanAnalysis(surveyData: any): void {
  (window as any).analyzeFloorPlan = () => {
    const result = analyzeFloorPlanSize(surveyData?.floor_plan);
    logFloorPlanAnalysis(result);
    return result;
  };

  (window as any).analyzeFloorPlanElements = () => {
    const elements = surveyData?.floor_plan?.linkedFloorPlanData?.elements;
    if (!elements) {
      console.log('❌ No elements found in floor plan data');
      return;
    }
    analyzeElementsArray(elements);
  };

  (window as any).findLargeFloorPlanElement = () => {
    const elements = surveyData?.floor_plan?.linkedFloorPlanData?.elements;
    if (!Array.isArray(elements)) {
      console.log('❌ No elements found');
      return;
    }

    console.log('🔎 Finding largest element...\n');

    // Find largest element
    let largestElement: any = null;
    let largestSize = 0;
    let largestIndex = -1;

    elements.forEach((element, idx) => {
      const size = getSize(element);
      if (size > largestSize) {
        largestSize = size;
        largestElement = element;
        largestIndex = idx;
      }
    });

    if (!largestElement) {
      console.log('❌ No large element found');
      return;
    }

    console.log(`🔴 LARGEST ELEMENT (Index ${largestIndex}):`);
    console.log(`   Size: ${formatBytes(largestSize)}`);
    console.log(`   Type: ${largestElement.type || 'unknown'}`);
    console.log(`   ID: ${largestElement.id || 'unknown'}`);
    console.log(`   Floor: ${largestElement.floor !== undefined ? largestElement.floor : 'unknown'}`);
    console.log(`\n   Keys: ${Object.keys(largestElement).join(', ')}`);

    // Check each field
    console.log(`\n   Field sizes:`);
    Object.keys(largestElement).forEach(key => {
      const fieldValue = largestElement[key];
      const fieldSize = getSize(fieldValue);
      if (fieldSize > 1000) { // Show fields > 1 KB
        console.log(`     ${key}: ${formatBytes(fieldSize)}`);
        
        if (typeof fieldValue === 'string') {
          if (fieldValue.startsWith('data:image')) {
            const format = fieldValue.substring(11, fieldValue.indexOf(';'));
            console.log(`       ⚠️ BASE64 IMAGE (${format})`);
          } else if (fieldValue.length > 100) {
            console.log(`       Sample: ${fieldValue.substring(0, 100)}...`);
          }
        }
      }
    });

    console.log(`\n💡 This element is causing ${formatBytes(largestSize)} of bloat!`);
    
    return {
      index: largestIndex,
      size: largestSize,
      element: largestElement
    };
  };
}
